import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

export const SITE_URL = 'https://voip2.co.uk';
const DEFAULT_TITLE = 'VOIP2 | Business Phone Numbers, Cloud PBX & SIP Trunks';
const DEFAULT_DESCRIPTION = 'Virtual phone numbers in 140+ countries, Cloud PBX, SIP trunking and Microsoft Teams calling from VOIP2, a UK business voice provider.';
const DEFAULT_IMAGE = `${SITE_URL}/media/site/hero-earth.webp`;

export const ORGANIZATION = {
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: 'VOIP2 Limited',
  alternateName: 'VOIP2',
  url: `${SITE_URL}/`,
  logo: `${SITE_URL}/icon-512.png`,
  email: 'hello@voip2.co.uk',
  telephone: '+44 204 575 2222',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '167-169 Great Portland Street, 5th Floor',
    addressLocality: 'London',
    postalCode: 'W1W 5PF',
    addressCountry: 'GB',
  },
  contactPoint: [{
    '@type': 'ContactPoint', contactType: 'sales', telephone: '+44 204 575 2222', email: 'hello@voip2.co.uk', areaServed: 'Worldwide',
    availableLanguage: 'English',
  }],
};

/** Absolute canonical URL for a path: no query string, no hash, no trailing slash (except the home page). */
export const canonicalUrl = (path) => {
  const p = (path || '/').split(/[?#]/)[0].replace(/\/+$/, '');
  return SITE_URL + (p || '/');
};

/** BreadcrumbList from [{ name, path }] (Home is added in front). */
export const breadcrumbLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [{ name: 'Home', path: '/' }, ...items].map((it, i) => ({
    '@type': 'ListItem', position: i + 1, name: it.name, item: canonicalUrl(it.path),
  })),
});

/** FAQPage from [{ q, a }] (answers as plain text). */
export const faqLd = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
});

/** Service schema for a product page, provided by VOIP2 Limited. No prices or ratings. */
export const serviceLd = ({ name, description, path, serviceType }) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name,
  serviceType: serviceType || name,
  description,
  url: canonicalUrl(path),
  provider: { '@id': `${SITE_URL}/#organization`, '@type': 'Organization', name: 'VOIP2 Limited', url: `${SITE_URL}/` },
  areaServed: 'Worldwide',
});

const safeJson = (o) => JSON.stringify(o).replace(/</g, '\\u003c');

/**
 * Page-level SEO tags: title ("… | VOIP2" is added unless the title already names VOIP2), description, canonical,
 * Open Graph, Twitter and JSON-LD. Every public page renders exactly one of these; react-helmet-async replaces the
 * defaults in public/index.html (they carry data-rh so they are swapped, not duplicated).
 *  - path: canonical path; defaults to the current path without query string
 *  - breadcrumbs: [{ name, path }] for a BreadcrumbList (Home is added)
 *  - jsonLd: one schema.org object or a list of them
 */
const Seo = ({ title, description, path, breadcrumbs, jsonLd, noindex = false, image, type = 'website' }) => {
  const location = useLocation();
  const fullTitle = !title ? DEFAULT_TITLE : /\bVOIP2\b/.test(title) ? title : `${title} | VOIP2`;
  const desc = description || DEFAULT_DESCRIPTION;
  const canonical = canonicalUrl(path || location.pathname);
  const img = image || DEFAULT_IMAGE;
  const ld = [
    ...(breadcrumbs && breadcrumbs.length ? [breadcrumbLd(breadcrumbs)] : []),
    ...(Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : []),
  ];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {noindex ? <meta name="robots" content="noindex, follow" /> : <link rel="canonical" href={canonical} />}
      <meta property="og:site_name" content="VOIP2" />
      <meta property="og:locale" content="en_GB" />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={img} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />
      {ld.map((o, i) => <script key={i} type="application/ld+json">{safeJson(o)}</script>)}
    </Helmet>
  );
};

export default Seo;
